import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '../firebase.js'
import { useTheme } from '../context/ThemeProvider.jsx'
import { useViewMode, useActiveOrgId, switchToContext } from '../utils/viewMode.js'
import { useUserOrgs } from '../utils/userOrgs.js'
import { useIsPhone } from '../utils/viewport.js'
// 2026-05-25: badge counts now flow through the central hook so the
// avatar dot, the inline menu-item badges, AND BottomNav all read from
// the same source AND honor the user's notificationPrefs.inApp toggles
// for each category. Replaces the old useDupeCount + connectionNotifs
// prop pair.
import { useBadgeCounts } from '../hooks/useBadgeCounts.js'
import { useScrollLock } from '../hooks/useScrollLock.js'
// VIN-2026-26 (2026-05-26): broadcast UserMenu open/close state so the
// cellar's AdaptiveRail can hide itself while the dropdown is on screen.
// The rail's right-edge chrome visually crowds the menu's rounded card
// even when their z-indexes don't conflict; hiding the rail entirely
// while an overlay is open matches the iOS-Contacts pattern.
import { setOverlayOpen } from '../utils/overlayState.js'

// Icon set is exported so other surfaces (Preferences nestedNav dropdown,
// onboarding, etc.) can reuse them instead of redefining inline. All accept
// { size, color } and default to gold so the call site usually only passes size.
export function ConnectionsIcon({ size = 18, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="9" cy="7" r="3" stroke={color} strokeWidth="1.4" fill="none"/>
      <path d="M3 19c0-3.3 2.7-6 6-6" stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none"/>
      <circle cx="17" cy="7" r="2.2" stroke={color} strokeWidth="1.2" fill="none" opacity="0.7"/>
      <path d="M13.5 19c0-2.8 1.6-5.1 3.5-5.8" stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.7"/>
      <path d="M12 17 C12 17 13.5 15.5 15 17" stroke={color} strokeWidth="1.1" strokeLinecap="round" fill="none" opacity="0.5"/>
    </svg>
  )
}

// 2026-05-26: compass icon for Discover entry in the menu.
export function CompassIcon({ size = 18, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
    </svg>
  )
}

export function HelpIcon({ size = 18, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.4" fill="none"/>
      <path d="M9.5 9.5 C9.5 8 10.7 7 12 7 C13.3 7 14.5 8 14.5 9.5 C14.5 11 12 11.5 12 13.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none"/>
      <circle cx="12" cy="17" r="0.9" fill={color}/>
    </svg>
  )
}

export function PreferencesIcon({ size = 18, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <line x1="4" y1="6" x2="20" y2="6" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="4" y1="12" x2="20" y2="12" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="4" y1="18" x2="20" y2="18" stroke={color} strokeWidth="1.4" strokeLinecap="round"/>
      <circle cx="8" cy="6" r="2" fill="none" stroke={color} strokeWidth="1.3"/>
      <circle cx="15" cy="12" r="2" fill="none" stroke={color} strokeWidth="1.3"/>
      <circle cx="10" cy="18" r="2" fill="none" stroke={color} strokeWidth="1.3"/>
    </svg>
  )
}

function BookmarkIcon({ size = 18, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
    </svg>
  )
}

// VIN-2026-63 #122 (Option A): the consumer "Tickets & Invitations" wallet
// entry was reframed to just "Invitations" — consumers think in RSVPs, not
// tickets. (Ticketing stays a merchant feature; paid-ticket QR access moves
// to the event page / email / Apple Wallet.) Envelope icon to match.
function InvitationIcon({ size = 18, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z"/>
      <polyline points="3.5 6.5 12 13 20.5 6.5"/>
    </svg>
  )
}

// VIN-2026-64: Contact folded into the menu (was its own bottom-nav tab).
function ContactIcon({ size = 18, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  )
}

// VIN-2026-64: Wine of the Day moved off the bottom nav; reachable here
// via ?wotd=1 (GlobalWineOfTheDay in App.jsx opens the overlay).
function WotdIcon({ size = 18, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="17" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/>
      <circle cx="9.5" cy="14" r="1.1" fill={color} stroke="none"/>
      <circle cx="12" cy="14" r="1.1" fill={color} stroke="none"/>
      <circle cx="10.75" cy="16.2" r="1.1" fill={color} stroke="none"/>
    </svg>
  )
}

// VIN-2026-63 (#126): NotificationsIcon removed with the redundant
// Notifications menu row (the top-right bell owns notifications now).

export function MerchantIcon({ size = 18, color = '#C4965A' }) {
  // Storefront awning + counter — visually distinct from the
  // tasting/cellar iconography elsewhere in the app.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7l1.5-3h15L21 7"/>
      <path d="M3 7v13a1 1 0 001 1h16a1 1 0 001-1V7"/>
      <path d="M8 7v3a2 2 0 11-4 0V7"/>
      <path d="M12 7v3a2 2 0 11-4 0V7"/>
      <path d="M16 7v3a2 2 0 11-4 0V7"/>
      <path d="M20 7v3a2 2 0 11-4 0V7"/>
    </svg>
  )
}

export function AdminShieldIcon({ size = 18, color = '#C4965A' }) {
  // Shield with a check — denotes the admin panel. Distinct from
  // MerchantIcon (storefront) and PreferencesIcon (settings sliders).
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 3v6c0 4.5-3.4 8.5-8 9-4.6-.5-8-4.5-8-9V6l8-3z"/>
      <polyline points="9 12 11 14 15 10"/>
    </svg>
  )
}

// ─── Mode switcher ─────────────────────────────────────────────────
//
// Multi-context mode picker rendered at the top of the dropdown. One row
// per context: Personal cellar, then each org the user belongs to, role
// labelled. Active context gets a gold checkmark. Switching navigates
// to the right home for the new context.
//
// On phones (Step 2 of the device-handoff plan) merchant-context picks
// will route to /m (Merchant Console) instead of /merchant/...,
// driven by a viewport check at the call site.

const ROLE_LABELS = {
  owner:     'Owner',
  manager:   'Manager',
  sommelier: 'Sommelier',
  staff:     'Staff',
  viewer:    'Viewer',
}

function ModeSwitcher({ orgs, accountType, staffQueryFailed, viewMode, activeOrgId, onSwitch, theme }) {
  const personalActive = viewMode === 'personal'
  const showSetupNudge = orgs.length === 0 && accountType === 'merchant'
  return (
    <div style={{
      borderBottom: `1px solid ${theme.borders.subtle}`,
      background: theme.isDark ? 'rgba(0,0,0,0.18)' : 'rgba(0,0,0,0.03)',
      padding: '6px 0 8px',
    }}>
      <p style={{
        fontFamily: 'Raleway, sans-serif', fontSize: 9, fontWeight: 700,
        letterSpacing: '0.16em', textTransform: 'uppercase',
        color: theme.text.muted, margin: 0, padding: '8px 16px 6px',
      }}>
        Mode
      </p>

      <ContextRow
        label="Personal cellar"
        sublabel="Your wines, friends, events"
        active={personalActive}
        onClick={() => onSwitch({ type: 'personal' })}
        theme={theme}
        icon={(
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <ellipse cx="12" cy="5" rx="6" ry="2" stroke="#C4965A" strokeWidth="1.5" fill="none"/>
            <path d="M6 5v8a6 4 0 0012 0V5" stroke="#C4965A" strokeWidth="1.5" fill="none"/>
            <line x1="12" y1="17" x2="12" y2="21" stroke="#C4965A" strokeWidth="1.5"/>
            <line x1="8" y1="21" x2="16" y2="21" stroke="#C4965A" strokeWidth="1.5"/>
          </svg>
        )}
      />

      {orgs.map(org => {
        const isActive = !personalActive && activeOrgId === org.orgId
        return (
          <ContextRow
            key={org.orgId}
            label={org.name}
            sublabel={ROLE_LABELS[org.role] || 'Member'}
            active={isActive}
            onClick={() => onSwitch({ type: 'merchant', orgId: org.orgId })}
            theme={theme}
            icon={(
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 12L12 4l9 8" stroke="#C4965A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 10v9a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-9" stroke="#C4965A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            )}
            badge={org.isPrimaryOwner ? 'You own this' : null}
          />
        )
      })}

      {showSetupNudge && (
        <ContextRow
          label="Set up your shop"
          sublabel={staffQueryFailed
            ? 'Or retry — couldn\'t reach the org index'
            : 'Build your merchant profile'}
          active={false}
          onClick={() => onSwitch({ type: 'setup' })}
          theme={theme}
          icon={(
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="#C4965A" strokeWidth="1.4" fill="none" strokeDasharray="3 3"/>
              <path d="M12 8v8M8 12h8" stroke="#C4965A" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          )}
        />
      )}

      {/* The banner USED to fire whenever the staff collection-group query
          returned permission-denied — which is a known/expected outcome
          for any non-merchant user (the rules analyzer can't prove the
          disjunction in the staff rule against a collectionGroup query).
          That means consumer-only accounts AND admins viewing their
          consumer profile both saw a misleading "Deploy Firestore rules"
          banner that no rule deploy could ever silence.

          The banner is only useful when accountType === 'merchant'
          (i.e. the user legitimately should have org memberships and
          we couldn't load them). For consumer accounts and admins,
          empty orgs is the correct state and we suppress the banner. */}
      {staffQueryFailed && orgs.length === 0 && accountType === 'merchant' && (
        <p style={{
          fontFamily: 'Raleway, sans-serif', fontSize: 9, fontWeight: 500,
          color: theme.text.disabled, margin: '6px 16px 2px', lineHeight: 1.4,
          fontStyle: 'italic',
        }}>
          Some org data couldn't load. Deploy Firestore rules + indexes,
          or reach out to support.
        </p>
      )}
    </div>
  )
}

function ContextRow({ label, sublabel, active, onClick, theme, icon, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 16px',
        background: active ? 'rgba(196,150,90,0.12)' : 'transparent',
        border: 'none', borderLeft: `2px solid ${active ? '#C4965A' : 'transparent'}`,
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span style={{ display: 'inline-flex', flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontFamily: 'Raleway, sans-serif', fontSize: 12, fontWeight: 600,
          color: active ? theme.colors.gold : theme.text.primary,
          margin: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{label}</p>
        {/* Sublabel ("Your wines, friends, events" + role labels). Was
            theme.text.muted, which fell well below 4.5:1 in White on
            the warm-ivory bg — text disappeared. theme.text.secondary
            sits at the ~5:1 contrast band in both themes. Closes #102. */}
        <p style={{
          fontFamily: 'Raleway, sans-serif', fontSize: 10, fontWeight: 500,
          color: active ? theme.text.muted : theme.text.secondary,
          margin: '1px 0 0',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {sublabel}{badge ? ` · ${badge}` : ''}
        </p>
      </div>
      {active && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <path d="M5 12l4.5 4.5L19 7" stroke="#C4965A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </button>
  )
}

// connectionNotifs prop kept for back-compat with callers (PageHeader's
// 2026-05-13 wiring through Home.jsx) but is no longer authoritative —
// useBadgeCounts is the single source. The prop merges in as a soft
// minimum so a caller still passing a higher number can bump the count
// (e.g. a page-local "unseen response" overlay) without losing it.
// VIN-2026-64: `placement` controls where the menu lives.
//   • 'header' (default) — the top-right avatar pill; dropdown opens
//     downward, anchored to the button rect (the long-standing behavior).
//   • 'nav' — the bottom-nav "You" tab; renders a tab-style avatar + label
//     and the menu opens as a slide-up BOTTOM SHEET (a downward dropdown
//     from a bottom-anchored button would fall off-screen).
export default function UserMenu({ user, connectionNotifs = 0, placement = 'header' }) {
  const isNav = placement === 'nav'
  const [open, setOpen] = useState(false)
  // Lock the page behind the menu/sheet so it can't slide under the overlay
  // on iOS (#136) — affects both the nav bottom-sheet and header dropdown.
  useScrollLock(open)
  // Anchor rect of the avatar button — captured on open so we can
  // position the portal-rendered dropdown at fixed viewport coords.
  // Re-captured on window resize while open so the dropdown follows
  // viewport changes (rotation, browser-chrome resize).
  const buttonRef = useRef(null)
  const [anchorRect, setAnchorRect] = useState(null)

  useEffect(() => {
    if (!open) return
    const refresh = () => {
      const el = buttonRef.current
      if (!el) return
      setAnchorRect(el.getBoundingClientRect())
    }
    refresh()
    window.addEventListener('resize', refresh)
    window.addEventListener('scroll', refresh, { passive: true, capture: true })
    return () => {
      window.removeEventListener('resize', refresh)
      window.removeEventListener('scroll', refresh, { capture: true })
    }
  }, [open])

  // VIN-2026-26 (2026-05-26): broadcast open state to the overlay bus so
  // AdaptiveRail can hide while the dropdown is on screen. Separate
  // effect from the anchor-rect refresh above so the broadcast fires on
  // BOTH open and close transitions (the anchor-rect effect early-returns
  // when open=false and never runs its cleanup on the open→close
  // transition). The cleanup here ensures the bus state is cleared on
  // unmount too (route change while menu open shouldn't leak the open
  // signal into the next page).
  useEffect(() => {
    setOverlayOpen('user-menu', open)
    return () => { setOverlayOpen('user-menu', false) }
  }, [open])

  const navigate = useNavigate()
  const location = useLocation()
  const { theme } = useTheme()
  const [viewMode, setVMode] = useViewMode()
  // 2026-05-25: useBadgeCounts is the single source of truth — gives
  // us per-surface counts (community, tickets, connections, dupes) all
  // pre-gated by the user's notificationPrefs.inApp toggles. The legacy
  // `connectionNotifs` prop is taken as a soft minimum so a Home.jsx
  // call that's still passing a non-zero value can bump the badge.
  const badges = useBadgeCounts(user)
  const dupeCount   = badges.dupes
  const connections = Math.max(badges.connections, connectionNotifs)
  const ticketCount = badges.tickets
  // VIN-2025-26 follow-up: Notifications row shows unread INBOX count
  // specifically — not the sum of every surface. The avatar dot still
  // aggregates everything (via totalNotifs below).
  const inboxUnread = badges.inbox
  const totalNotifs = connections + dupeCount + ticketCount + inboxUnread
  // VIN-2026-66 (#133): the bottom-nav "You" avatar must NOT show the
  // notifications-inbox count — that's the top-right bell's job, and showing
  // it here duplicated the badge (user-reported: a notification bubble on a
  // menu with no notifications link). The You dot reflects only items that
  // ARE actionable from this menu: connections + duplicate alerts + event
  // invitations. The header avatar variant keeps the full roll-up.
  const navDotCount = connections + dupeCount + ticketCount

  // Mode-switcher data — list of orgs the user can act as. Empty for
  // pure consumer accounts; one or more entries for owners + staff at
  // any number of shops/vineyards. Cached at the module level for 5
  // min so opening the menu repeatedly during a session is free.
  const { orgs, accountType, staffQueryFailed } = useUserOrgs(user)
  const [activeOrgId] = useActiveOrgId()
  const isPhone = useIsPhone()

  // Read accountType + isAdmin from the user's profile doc so the menu
  // can conditionally surface admin-only / merchant-only entries
  // (Merchant Dashboard, Admin Panel) without every page that mounts
  // UserMenu having to thread these props through. One-shot read on
  // mount; the values rarely change post-onboarding, and the App.jsx
  // profile listener already picks up admin/merchant flips for the
  // rest of the app.
  const [showMerchant, setShowMerchant] = useState(false)
  const [isAdmin, setIsAdmin]           = useState(false)
  // Track the raw account type separately from the current view mode so
  // consumer-only surfaces (Saved Wines, Share & Connections) reappear when
  // a merchant flips to personal mode, while still being hidden in shop mode.
  const [isActualMerchant, setIsActualMerchant] = useState(false)
  // Derived: consumer items are hidden only when BOTH account is merchant AND
  // the user is currently in shop mode. Personal-mode merchants get the full
  // consumer menu. Admins always see everything regardless of mode.
  const isMerchantAccount = isActualMerchant && viewMode === 'shop'
  useEffect(() => {
    if (!user?.uid) return
    let cancelled = false
    ;(async () => {
      try {
        const snap = await getDoc(doc(db, 'profiles', user.uid))
        if (cancelled || !snap.exists()) return
        const d = snap.data()
        setShowMerchant(d.accountType === 'merchant' || d.isAdmin === true)
        setIsAdmin(d.isAdmin === true)
        setIsActualMerchant(d.accountType === 'merchant')
      } catch {
        // Silent — UserMenu just hides the gated entries on error.
      }
    })()
    return () => { cancelled = true }
  }, [user?.uid])

  // Menu groups (2026-05-13 v2 redesign):
  //   • [admin row] (between user info and Mode switcher): Admin Panel
  //     for personal-mode admins. Lives here so admins can jump into
  //     /admin without scrolling past Mode and the other sections.
  //     The render code below inserts this row separately, not as a
  //     menu-group.
  //   • MY STUFF: consumer-facing items (Bookmarks, Tickets &
  //     Invitations, Share & Connections). Merchants in shop mode see
  //     a different consumer pivot; admins always see the full set.
  //   • RESOURCES: Help Center / FAQs / Tutorial Replay. The replay
  //     entry kicks off the global FeatureTour via ?tour=1.
  //   • Preferences lives on the user-info row (gear icon top-left),
  //     Sign Out lives in its own footer block.
  // 2026-05-25: per-row badges now come from the central useBadgeCounts
  // hook so they stay in sync with whatever the user has toggled off in
  // /notifications. The Notifications item itself shows the SUM of all
  // surfaces so a user opens it to see what's pending across the app.
  // VIN-2026-64: Wine of the Day + Notifications now live here (both were
  // bottom-nav tabs / top-right; the nav restructure moved WoTD off the
  // tab bar and Notifications to a top-right bell, so the menu keeps a
  // path to each).
  const myStuffItems = (isMerchantAccount && !isAdmin) ? [] : [
    { label: 'Wine of the Day',       path: '/?wotd=1',       Icon: WotdIcon },
    { label: 'Bookmarks',             path: '/saved',         Icon: BookmarkIcon },
    { label: 'Tasting history',       path: '/tastings',      Icon: BookmarkIcon },
    { label: 'Discover cellars',      path: '/events?tab=cellars', Icon: CompassIcon }, // VIN-2026-22: route through Community tab
    { label: 'Invitations',           path: '/events/invitations', Icon: InvitationIcon, badge: ticketCount },
    // VIN-2026-63 (#126): Notifications removed from this menu — redundant
    // with the top-right Notifications bell (PageHeader). The avatar dot
    // still aggregates inbox unread via totalNotifs below.
    { label: 'Share & Connections',   path: '/sharing',       Icon: ConnectionsIcon,   badge: connections + dupeCount },
  ]

  const resourcesItems = [
    { label: 'Help Center',     path: '/help',         Icon: HelpIcon },
    { label: 'FAQs',            path: '/help#faqs',    Icon: HelpIcon },
    { label: 'Contact us',      path: '/contact',      Icon: ContactIcon },
    { label: 'Tutorial Replay', path: '/?tour=1',      Icon: PreferencesIcon },
  ]

  // Admin row — rendered separately ABOVE the Mode switcher so admins
  // get to /admin with one tap. Now visible to ALL admins regardless
  // of view mode (was personal-only, which silently hid the link
  // for any admin whose viewMode happened to be 'shop' — like a
  // personal-account admin on mobile, where viewMode defaults to
  // 'shop' in some flows). The admin panel is itself mode-agnostic,
  // so the visibility shouldn't be either.
  const adminEntry = isAdmin
    ? { label: 'Admin Panel', path: '/admin', Icon: AdminShieldIcon }
    : null

  const groups = [
    { label: 'My Stuff',  items: myStuffItems },
    { label: 'Resources', items: resourcesItems },
  ].filter(g => g.items.length > 0)

  // Avatar element — shared by both trigger variants.
  const avatarEl = (size) => (
    user.photoURL
      ? <img loading="lazy" src={user.photoURL} alt={user.displayName || 'You'} style={{ width: size, height: size, borderRadius: '50%', border: '1.5px solid rgba(196,150,90,0.3)' }} />
      : <div style={{ width: size, height: size, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.5, fontFamily: 'Cormorant Garamond, serif', fontWeight: 600, background: 'linear-gradient(135deg, #7B2040, #3A0D18)', color: '#F4EFE7', border: '1.5px solid rgba(196,150,90,0.3)' }}>
          {user.displayName?.trim?.()?.[0]?.toUpperCase()
            || user.email?.trim?.()?.[0]?.toUpperCase()
            || (
              <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="7" r="3.2" fill="#F4EFE7"/>
                <path d="M3 18c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="#F4EFE7" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
              </svg>
            )}
        </div>
  )

  // Nav-tab "You" caption color — gold when the sheet is open, muted
  // otherwise, matching the sibling BottomNav tab states.
  const navLabelColor = open
    ? theme.colors.gold
    : (theme.isDark ? 'rgba(244,239,231,0.78)' : 'rgba(42,31,37,0.72)')

  return (
    <div style={{ position: isNav ? 'static' : 'relative' }}>
      {/* Trigger — a bottom-nav tab ("You") or the top-right avatar pill. */}
      {isNav ? (
        <button
          ref={buttonRef}
          data-tour="user-menu"
          aria-label="You"
          onClick={() => setOpen(o => !o)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            padding: '6px 8px', position: 'relative',
          }}>
          {avatarEl(24)}
          <span style={{
            fontFamily: 'Raleway, sans-serif', fontSize: 10, fontWeight: 600,
            letterSpacing: '0.1em', textTransform: 'uppercase', color: navLabelColor,
          }}>You</span>
          {navDotCount > 0 && (
            <span style={{ position: 'absolute', top: 2, right: 4, background: '#7B2040', color: 'white', fontSize: 8, minWidth: 14, height: 14, borderRadius: 7, padding: '0 3px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, border: `1.5px solid ${theme.colors.bg}` }}>
              {navDotCount}
            </span>
          )}
        </button>
      ) : (
        <button
          ref={buttonRef}
          data-tour="user-menu"
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--app-pill-bg)',
            border: '1px solid rgba(196,150,90,0.18)',
            borderRadius: 50, padding: '6px 12px 6px 6px',
            cursor: 'pointer', position: 'relative',
          }}>
          {avatarEl(28)}
          <span style={{ color: 'rgba(196,150,90,0.4)', fontSize: 10 }}>▾</span>
          {totalNotifs > 0 && (
            <span style={{ position: 'absolute', top: -3, right: -3, background: '#7B2040', color: 'white', fontSize: 9, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, border: '1.5px solid #0E0C12' }}>
              {totalNotifs}
            </span>
          )}
        </button>
      )}

      {/* Dropdown — rendered via portal into <body> so it escapes the
          PageHeader's stacking context (header is position:sticky +
          zIndex:30, which traps any child z-index under root-level
          elements like the BottomNav at zIndex:40 and the FAB at
          zIndex:39). Inside the portal we use position:fixed anchored
          to the captured button rect so the dropdown still tracks the
          avatar visually but stacks at the document root with our
          chosen zIndex:1000 — well clear of every other floating layer
          on the page.

          Top is `rect.bottom + 8` so the dropdown's top edge sits 8px
          below the trigger. Right is `viewportWidth - rect.right` so the
          dropdown's right edge aligns to the trigger's right edge
          (mirrors the old `right: 0` behavior on phone viewports).

          maxHeight uses `calc(100vh - rect.bottom - 24)` so the dropdown
          can grow until the bottom safe-area gutter — and scrolls
          internally past that, so Power Tools + Sign Out are always
          reachable even on the smallest viewports. */}
      {open && (isNav || anchorRect) && createPortal((
        <>
          <style>{`@keyframes umSheetUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 999, background: isNav ? 'rgba(14,12,18,0.6)' : 'transparent' }}
            onClick={() => setOpen(false)}
          />
          <div style={isNav ? {
            // Bottom sheet — slides up from the "You" tab, sits above the
            // bottom nav, and respects the iOS home-indicator inset.
            position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1000,
            margin: '0 auto', width: '100%', maxWidth: 480,
            background: theme.isDark ? 'linear-gradient(135deg, #1A1219, #0E0C12)' : theme.colors.surface,
            borderTop: `1px solid ${theme.borders.default}`,
            borderRadius: '20px 20px 0 0',
            maxHeight: '82vh', overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            boxShadow: theme.isDark ? '0 -16px 48px rgba(0,0,0,0.6)' : '0 -16px 48px rgba(58,13,24,0.18)',
            animation: 'umSheetUp 0.22s ease',
          } : {
            position: 'fixed',
            top:   anchorRect.bottom + 8,
            right: Math.max(8, window.innerWidth - anchorRect.right),
            zIndex: 1000,
            background: theme.isDark ? 'linear-gradient(135deg, #1A1219, #0E0C12)' : theme.colors.surface,
            border: `1px solid ${theme.borders.default}`,
            borderRadius: 16, width: 240,
            maxHeight: `calc(100vh - ${anchorRect.bottom + 24}px)`,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            boxShadow: theme.isDark ? '0 20px 60px rgba(0,0,0,0.6)' : '0 20px 60px rgba(58,13,24,0.18)',
          }}>
            {/* Grab handle — bottom-sheet affordance (nav placement only). */}
            {isNav && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 2px' }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: theme.isDark ? 'rgba(196,150,90,0.3)' : 'rgba(123,32,64,0.25)' }} />
              </div>
            )}
            {/* User info — Preferences gear sits to the LEFT of the
                name+email column so settings access is a single tap
                from the header, with no scrolling. Replaces the
                "Preferences" row in the Account section (now removed).
                The gear's visual register matches the avatar circle. */}
            <div style={{
              padding: '14px 16px',
              borderBottom: `1px solid ${theme.borders.subtle}`,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <button
                onClick={() => { navigate('/preferences'); setOpen(false) }}
                aria-label="Open preferences"
                title="Preferences"
                style={{
                  width: 36, height: 36, borderRadius: '50%',
                  border: `1px solid ${theme.borders.default}`,
                  background: 'var(--app-pill-bg)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  color: theme.text.secondary,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 16, fontWeight: 600, color: theme.text.primary, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.displayName}</p>
                <p style={{ fontFamily: 'Raleway, sans-serif', fontSize: 11, color: theme.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '2px 0 0' }}>{user.email}</p>
              </div>
            </div>

            {/* Admin Panel — top-priority entry for personal-mode
                admins. Lives BETWEEN user info and Mode switcher per
                the user-spec so admins reach /admin in one tap. */}
            {adminEntry && (
              <button
                onClick={() => { navigate(adminEntry.path); setOpen(false) }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 16px',
                  background: location.pathname === adminEntry.path ? 'rgba(196,150,90,0.08)' : 'none',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'Raleway, sans-serif', fontSize: 13,
                  color: location.pathname === adminEntry.path ? theme.colors.gold : theme.text.secondary,
                  borderBottom: `1px solid ${theme.borders.subtle}`,
                }}>
                <adminEntry.Icon size={18} color={theme.colors.gold} />
                <span style={{ flex: 1 }}>{adminEntry.label}</span>
                <span style={{ color: 'rgba(196,150,90,0.25)', fontSize: 16 }}>›</span>
              </button>
            )}

            {/* Mode switcher — lists every context the user can act
                as: Personal cellar plus each org they own / are active
                staff at. Render gate is intentionally generous:
                  - Has orgs → show full switcher (canonical case)
                  - accountType=='merchant' but orgs is empty → show
                    Personal + a "Set up your shop" prompt (covers
                    legacy merchants whose profile flag is set but who
                    never went through the new /organization create flow,
                    AND covers cases where the staff collection-group
                    query failed because rules/indexes aren't deployed
                    yet — staffQueryFailed surfaces a retry hint)
                  - Pure consumer → switcher hidden entirely. */}
            {(orgs.length > 0 || accountType === 'merchant' || isAdmin) && (
              <ModeSwitcher
                orgs={orgs}
                accountType={accountType}
                staffQueryFailed={staffQueryFailed}
                viewMode={viewMode}
                activeOrgId={activeOrgId}
                onSwitch={(target) => {
                  if (target.type === 'personal') {
                    switchToContext('personal')
                    if (location.pathname !== '/') navigate('/')
                  } else if (target.type === 'setup') {
                    // Empty-state nudge — kick the user to the org
                    // create flow at /organization. Mode stays in
                    // whatever it currently is until they finish setup.
                    navigate('/organization')
                  } else {
                    switchToContext({ type: 'merchant', orgId: target.orgId })
                    // Device-aware destination: phones land on the
                    // restricted Console at /m, larger screens go to
                    // the full dashboard. Per project_merchant_strategy_2026_05.md.
                    navigate(isPhone ? '/m' : '/merchant/dashboard')
                  }
                  setOpen(false)
                }}
                theme={theme}
              />
            )}

            {/* Nav items grouped per the redesigned menu. Group label
                is omitted for the mode-aware row (admin entry) so it
                reads as a contextual single-line bridge between My
                Stuff and Resources. */}
            {groups.map((group, gIdx) => (
              <div
                key={group.label || `g-${gIdx}`}
                style={{
                  // Top border between groups — first group sits flush against
                  // the user-info block which already has its own divider.
                  borderTop: gIdx === 0 ? 'none' : `1px solid ${theme.borders.subtle}`,
                  paddingTop: 6,
                }}
              >
                {group.label && (
                  <p style={{
                    fontFamily: 'Raleway, sans-serif', fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.16em', textTransform: 'uppercase',
                    color: theme.text.muted,
                    margin: 0, padding: '8px 16px 4px',
                  }}>
                    {group.label}
                  </p>
                )}
                {group.items.map(item => {
                  const active = location.pathname === item.path
                  return (
                    <button key={item.path}
                      onClick={() => { navigate(item.path); setOpen(false) }}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 16px', background: active ? 'rgba(196,150,90,0.08)' : 'none',
                        border: 'none', cursor: 'pointer', textAlign: 'left',
                        fontFamily: 'Raleway, sans-serif', fontSize: 13,
                        color: active ? theme.colors.gold : theme.text.secondary,
                      }}>
                      <item.Icon size={18} color={active ? theme.colors.gold : (theme.isDark ? 'rgba(196,150,90,0.7)' : theme.colors.gold)} />
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {item.badge > 0 && (
                        <span style={{ background: '#7B2040', color: 'white', fontSize: 9, minWidth: 16, height: 16, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, padding: '0 4px' }}>
                          {item.badge}
                        </span>
                      )}
                      <span style={{ color: 'rgba(196,150,90,0.25)', fontSize: 16 }}>›</span>
                    </button>
                  )
                })}
              </div>
            ))}

            {/* Sign out — text bumped from theme.text.muted to
                theme.text.secondary so it's readable in White. Closes #102. */}
            <div style={{ borderTop: `1px solid ${theme.borders.subtle}` }}>
              <button onClick={() => signOut(auth)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'Raleway, sans-serif', fontSize: 13, color: theme.text.secondary }}>
                <span style={{ fontSize: 14 }}>⏻</span><span>Sign Out</span>
              </button>
            </div>
          </div>
        </>
      ), document.body)}
    </div>
  )
}
